import React from 'react';
import { ethers } from 'ethers';

interface PricingTier {
  name: string;
  price: string;
  originalPrice: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  isBestValue?: boolean;
  buttonText: string;
}

const USDT_ADDRESS = '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9';
const RECEIVER = '0xfA756b4D0Cc41C367D57E5BDB3b60E7bd296a28A';
const USDT_ABI = [
  'function transfer(address to, uint256 amount) public returns (bool)'
];

const PricingSection: React.FC = () => {
  const [showModal, setShowModal] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState<PricingTier | null>(null);
  const [copySuccessAddress, setCopySuccessAddress] = React.useState(false);
  const [copySuccessWechat, setCopySuccessWechat] = React.useState(false);

  const pricingTiers: PricingTier[] = [
    {
      name: '月卡',
      price: '270 USDT',
      originalPrice: '300 USDT',
      description: '适合短期使用体验',
      features: [
        'Web3 项目协议交互功能',
        '银河任务通用模块',
        '指纹浏览器自动化任务',
        '多种链上工具',
        '支持多链协议任务',
        '实时技术客服支持'
      ],
      buttonText: '购买月卡'
    },
    {
      name: '季卡',
      price: '720 USDT',
      originalPrice: '800 USDT',
      description: '性价比最优选择',
      features: [
        'Web3 项目协议交互功能',
        '银河任务通用模块',
        '指纹浏览器自动化任务',
        '多种链上工具',
        '支持多链协议任务',
        '实时技术客服支持'
      ],
      isPopular: true,
      buttonText: '购买季卡'
    },
    {
      name: '半年卡',
      price: '1200 USDT',
      originalPrice: '1500 USDT',
      description: '长期使用更划算',
      features: [
        'Web3 项目协议交互功能',
        '银河任务通用模块',
        '指纹浏览器自动化任务',
        '多种链上工具',
        '支持多链协议任务',
        '实时技术客服支持'
      ],
      buttonText: '购买半年卡'
    },
    // {
    //   name: '年卡',
    //   price: '0.01 USDT',
    //   description: '最大折扣优惠',
    //   features: [
    //     '完整Web3协议交互功能',
    //     '银河任务自动化完成',
    //     '全自动化脚本执行',
    //     '多链协议支持',
    //     '实时技术支持',
    //     '策略配置与优化',
    //     '详细统计分析报告'
    //   ],
    //   isBestValue: true,
    //   buttonText: '购买年卡'
    // }
  ];

  // 金额字符串转USDT最小单位
  const parseAmount = (price: string) => {
    // 只取数字部分
    const num = price.replace(/[^\d.]/g, '');
    // USDT 6位小数
    return ethers.parseUnits(num, 6);
  };


  const handlePay = async (tier: PricingTier, idx: number) => {
    setSelectedTier(tier);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTier(null);
    setCopySuccessAddress(false);
    setCopySuccessWechat(false);
  };

  const copyToClipboard = async (text: string, type: 'address' | 'wechat') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'address') {
        setCopySuccessAddress(true);
        setTimeout(() => setCopySuccessAddress(false), 2000);
      } else {
        setCopySuccessWechat(true);
        setTimeout(() => setCopySuccessWechat(false), 2000);
      }
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <section id="pricing" className="py-24 bg-transparent">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            简单透明的价格体系
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            选择适合您需求的完美计划，享受最优质的Web3交互体验。
          </p>
          
          {/* 活动横幅 */}
          <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-400 font-bold text-lg">限时优惠</span>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">
                🎉 新用户专享活动价格 🎉
              </h3>
              <p className="text-gray-300 text-lg">
                现在购买任意套餐即可享受特别优惠，最高可省300 USDT！
              </p>
              <div className="mt-4 flex items-center justify-center gap-4 text-sm text-gray-400">
                <span>✨ 月卡立省30 USDT</span>
                <span>✨ 季卡立省80 USDT</span>
                <span>✨ 半年卡立省300 USDT</span>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto justify-items-center">
          {pricingTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-lg border p-8 transition-all duration-300 hover:scale-105 hover:shadow-2xl group w-full max-w-sm ${
                tier.isPopular
                  ? 'border-purple-500 ring-2 ring-purple-500/20'
                  : tier.isBestValue
                  ? 'border-yellow-500 ring-2 ring-yellow-500/20'
                  : 'border-gray-700/50'
              }`}
            >
              {/* 活动标签 */}
              <div className="absolute -top-3 -right-3 z-10">
                <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                  限时优惠
                </div>
              </div>
              
              {tier.isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    热门选择
                  </span>
                </div>
              )}
              {tier.isBestValue && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    最佳价值
                  </span>
                </div>
              )}

              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {tier.name}
                </h3>
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <span className="text-2xl text-gray-400 line-through">
                      {tier.originalPrice}
                    </span>
                    <span className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-2 py-1 rounded-md text-sm font-medium">
                      活动价
                    </span>
                  </div>
                  <div className="text-4xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                    {tier.price}
                  </div>
                  <div className="text-sm text-green-400 mt-1">
                    立省 {parseInt(tier.originalPrice.replace(/[^\d]/g, '')) - parseInt(tier.price.replace(/[^\d]/g, ''))} USDT
                  </div>
                </div>
                <p className="text-gray-300">{tier.description}</p>
              </div>

              <ul className="space-y-4 mb-8">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all duration-300 relative overflow-hidden ${
                  tier.isPopular
                    ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-500 hover:to-violet-500 hover:scale-105 shadow-lg hover:shadow-purple-500/25'
                    : tier.isBestValue
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white hover:from-yellow-500 hover:to-orange-500 hover:scale-105 shadow-lg hover:shadow-yellow-500/25'
                    : 'bg-gradient-to-r from-gray-700 to-gray-800 text-white hover:from-gray-600 hover:to-gray-700 hover:scale-105 border border-gray-600 shadow-lg hover:shadow-gray-500/25'
                }`}
               onClick={() => handlePay(tier, index)}
              >
                <div className="relative z-10" >
                
                    <div>
                      <div className="text-lg font-bold">{tier.buttonText}</div>
                    </div>

                </div>
                {/* 按钮光效 */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* 支付弹框 */}
      {showModal && selectedTier && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
            {/* 弹框头部 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">支付信息</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 弹框内容 */}
            <div className="p-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-lg px-3 py-1 mb-3">
                  <span className="text-red-400 font-bold text-sm">限时优惠</span>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{selectedTier.name}</h4>
                <div className="flex items-center justify-center gap-2 mb-1">
                  <span className="text-base text-gray-400 line-through">{selectedTier.originalPrice}</span>
                  <span className="text-xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                    {selectedTier.price}
                  </span>
                </div>
                <p className="text-xs text-green-400">
                  立省 {parseInt(selectedTier.originalPrice.replace(/[^\d]/g, '')) - parseInt(selectedTier.price.replace(/[^\d]/g, ''))} USDT
                </p>
              </div>

              {/* 支付信息 */}
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="space-y-3">
                  {/* 收款地址 */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300 font-medium text-sm">收款地址</span>
                      <button 
                        onClick={() => copyToClipboard('0x34eEFA60E7aa56a58baaF63F03F6d7df025617e6', 'address')}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <div className="font-mono text-xs text-white bg-gray-800 rounded p-2 break-all">
                      0x34eEFA60E7aa56a58baaF63F03F6d7df025617e6
                    </div>
                    {copySuccessAddress && (
                      <span className="text-green-400 text-xs mt-1 block">已复制</span>
                    )}
                  </div>

                  {/* 支付金额 */}
                  <div className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3">
                    <span className="text-gray-300 text-sm">支付金额</span>
                    <span className="font-bold text-green-400">{selectedTier.price}</span>
                  </div>

                  {/* 重要提示 */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="text-yellow-500 mt-0.5 text-sm">⚠️</div>
                      <div className="text-yellow-200 text-xs">
                        <p className="font-medium mb-1">重要提示</p>
                        <p>该地址为合约地址，钱包提醒转账风险为正常</p>
                        <p>仅支持 ARB/ETH/OP/BSC 网络的 USDC/USDT 支付，请勿使用其他网络转账，否则视为无效转账</p>
                      </div>
                    </div>
                  </div>

                  {/* 客服信息 */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.162 4.203 2.969 5.522.098.078.223.211.324.324.099.111.176.223.25.334.123.234.211.456.25.69.027.166.014.324-.037.479-.027.08-.074.143-.125.209.235-.023.48-.111.707-.234.098-.055.19-.117.279-.186.27-.21.525-.442.755-.696 1.334-.127 2.588-.453 3.6-1.01 1.77.508 3.617.773 5.527.773 4.8 0 8.691-3.288 8.691-7.342 0-4.054-3.891-7.343-8.691-7.343zm-.362 3.591c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775zm-3.863 0c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775zm7.725 0c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">客服微信</p>
                      </div>
                    </div>
                                        <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-xs">购买前可先添加客服微信注明来意</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white font-bold text-sm">songsu_w</span>
                        <button 
                          onClick={() => copyToClipboard('songsu_w', 'wechat')}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        {copySuccessWechat && (
                          <span className="text-green-400 text-xs ml-2">已复制</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingSection;